import React from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { CurveMark } from "../../components/CarparkCurve";
import AdminErrorBoundary from "./AdminErrorBoundary";
import { BarChart3, Inbox, FileText, Briefcase, MessageCircle, LogOut, ExternalLink, GraduationCap, Quote, Award, Activity, Image as ImageIcon, Type, Package } from "lucide-react";

const nav = [
  { to: "/admin", label: "Dashboard", icon: BarChart3, end: true },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/diagnostics", label: "Diagnostics", icon: Activity },
  { to: "/admin/conversations", label: "Conversations", icon: MessageCircle },
  { to: "/admin/case-studies", label: "Case Studies", icon: FileText },
  { to: "/admin/services", label: "Services", icon: Briefcase },
  { to: "/admin/experience", label: "Experience", icon: GraduationCap },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/awards", label: "Awards", icon: Award },
  { to: "/admin/media", label: "Assets", icon: ImageIcon },
  { to: "/admin/event-photos", label: "Event Photos", icon: ImageIcon },
  { to: "/admin/site-content", label: "Site Content", icon: Type },
  { to: "/brand-kit", label: "Brand Kit", icon: Package, external: true },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D8A936] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user || !user.is_admin) {
    return <Navigate to="/admin/login" replace />;
  }

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] text-[#FAF9F6] flex flex-col lg:flex-row" data-testid="admin-layout">
      <aside className="lg:w-64 border-r border-[#343437] lg:min-h-screen bg-[#0B0B0C] flex flex-col">
        <div className="p-6 border-b border-[#343437] flex items-center gap-3">
          <CurveMark className="w-[52px] h-[30px] text-[#FAF9F6]" />
          <div>
            <p className="font-display font-extrabold text-[13px] tracking-[0.22em] uppercase">JJ Argent</p>
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#D8A936]">Admin Console</p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1" aria-label="Admin nav">
          {nav.map(({ to, label, icon: Icon, end, external }) => {
            const testid = `admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`;
            if (external) {
              return (
                <a
                  key={to}
                  href={to}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={testid}
                  className="flex items-center gap-3 px-4 py-3 font-display text-[12px] tracking-[0.16em] uppercase border border-transparent text-[#A9A9A6] hover:border-[#343437] hover:text-[#FAF9F6] transition-colors"
                >
                  <Icon size={14} /> {label} <ExternalLink size={10} className="ml-auto opacity-60" />
                </a>
              );
            }
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 font-display text-[12px] tracking-[0.16em] uppercase border transition-colors ${
                    isActive
                      ? "border-[#D8A936] text-[#D8A936] bg-[#D8A936]/5"
                      : "border-transparent text-[#A9A9A6] hover:border-[#343437] hover:text-[#FAF9F6]"
                  }`
                }
              >
                <Icon size={14} /> {label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-[#343437] space-y-3">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 text-xs text-[#A9A9A6] hover:text-[#D8A936] transition-colors"
          >
            <ExternalLink size={12} /> View public site
          </a>
          <div className="border border-[#343437] p-3">
            <p className="text-xs text-[#A9A9A6] truncate" data-testid="admin-user-email">{user.email}</p>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="mt-3 w-full flex items-center justify-center gap-2 py-2 border border-[#343437] text-xs tracking-widest uppercase hover:border-[#D8A936] hover:text-[#D8A936] transition-colors"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 p-6 lg:p-10 max-w-full overflow-x-hidden">
        <AdminErrorBoundary key={location.pathname}>
          <Outlet />
        </AdminErrorBoundary>
      </main>
    </div>
  );
}
